import { lazy, Suspense } from "react";
import { Navigate, Outlet, useRoutes } from 'react-router-dom';
import ProtectedRoute from "./ProtectedRoute";
import PageLoader from "../shared/PageLoader/PageLoader";
import FAQs from "../web/FAQs/FAQs";
import Disclaimer from "../web/Disclaimer/Disclaimer";
import CopyrightPolicy from "../web/CopyrightPolicy/CopyRightPolicy";
import HyperLinkingPolicy from "../web/HyperLinkingPolicy/HyperLinkingPolicy";
import PrivacyPolicy from "../web/PrivacyPolicy/PrivacyPolicy";
import LinksByOtherWebsites from "../web/LinksByOtherWebsites/LinksByOtherWebsites";
import Profile from "../pages/Admin/Profile/Profile";
import PositionTracker from "../pages/Admin/PositionTracker/PositionTracker";
import PositionTrackerListing from "../pages/Admin/PositionTracker/PositionTrackerListing/PositionTrackerListing";
import RaiseQuery from "../pages/Admin/PositionTracker/RaiseQuery/RaiseQuery";
import InterviewCalendar from "../pages/Admin/InterviewCalendar/InterviewCalendar";
import InterviewCalendarList from "../pages/Admin/InterviewCalendar/InterviewCalendarList/InterviewCalendarList";
import QueryTracker from "../pages/Admin/QueryTracker/QueryTracker";
import QueryTrackerListing from "../pages/Admin/QueryTracker/QueryTrackerListing/QueryTrackerListing";
import SopAndGuideline from "../pages/Admin/SopAndGuideline/SopAndGuideline";
import SopAndGuidelineListing from "../pages/Admin/SopAndGuideline/SopAndGuideLineListing/SopAndGuidelineListing";
import InterviewCalendarView from "../pages/Admin/InterviewCalendar/InterviewCalendarView/InterviewCalendarView";
import InterviewTrackerView from "../pages/Admin/Interviewer/InterviewTrackerListing/InterviewTrackerView";
import SystemNotificationListing from "../pages/Admin/SystemNotification/SystemNotificationListing";
import CandidateManagementUpcomingView from "../pages/Admin/CandidateManagement/CandidateManagementView/CandidateManagementUpcomingView";
import CandidateManageMentRescheduleInterview from "../pages/Admin/CandidateManagement/CandidateManagementView/CandidateManageMentRescheduleInterview";
import CandidateManagementCalenderView from "../pages/Admin/CandidateManagement/CandidateManagementView/CandidateManagementCalenderView";
import CandidateMangementPositionView from "../pages/Admin/CandidateManagement/CandidateManagementView/CandidateMangementPositionView";
import InterviewTrackers from "../pages/Admin/InterviewTracker/InterviewTracker";
import InterviewTrackerListings from "../pages/Admin/InterviewTracker/InteviewTrackerListing/InterviewTrackerListing";
import InterviewTrackerViews from "../pages/Admin/InterviewTracker/InteviewTrackerListing/InterviewTrackerView";
import QueryTrackerView from "../pages/Admin/QueryTracker/QueryTrackerView/QueryTrackerView";
import QueryTrackerRespond from "../pages/Admin/QueryTracker/QueryTrackerRespond/QueryTrackerRespond";
import CandidatePool from "../pages/Admin/CandidatePool/CandidatePool";
import EligiblityComparision from "../pages/Admin/Interviewer/InterviewTrackerListing/EligiliblityComparision/EligiblityComparision";
import { Provider } from "react-redux";
import SkipToMainContentStore from "../layouts/WebLayout/SkipToMainContent/SkipToMainContentStore";
import CandidateInterviewTrackerListing from "../pages/Admin/InterviewTracker/candidate/CandidateInterviewTrackerListing";
import CandidateInterviewTrackerView from "../pages/Admin/InterviewTracker/candidate/CandidateInterviewTrackerView";
import CandidateInterviewTracker from "../pages/Admin/InterviewTracker/candidate/CandidateInterviewTracker";
import CheckPermission from "./CheckPermission";
import { decryptAESData } from "../utils/types";

// Web Pages
const HomePage = lazy(() => import("../web/Home/Home"));
const AboutUsPage = lazy(() => import("../web/AboutUs/AboutUs"));
const ContactUsPage = lazy(() => import("../web/ContactUs/ContactUs"));
const ExplorePositionsPage = lazy(() => import("../web/ExplorePositions/ExplorePositions"));
const SOPPage = lazy(() => import("../web/SOP/SOP"));
const TermsPage = lazy(() => import("../web/Terms/Terms"));


// Layout Pages
const AuthLayout = lazy(() => import("../layouts/AuthLayout/AuthLayout"));
const AdminLayout = lazy(() => import("../layouts/AdminLayout/AdminLayout"));
const WebLayout = lazy(() => import("../layouts/WebLayout/WebLayout"));


// Auth Pages
const LoginPage = lazy(() => import("../pages/Auth/Login/LoginPage"));
const ForgotPassword = lazy(() => import("../pages/Auth/ForgotPassword/ForgotPassword"));
const RestPassword = lazy(() => import("../pages/Auth/RestPassword/RestPassword"));

// Admin Pages
const AdminChangePassword = lazy(() => import("../pages/Admin/ChangePassword/ChangePassword"));
const Dashboard = lazy(() => import("../pages/Admin/Dashboard/Dashboard"));
const UserManagement = lazy(() => import("../pages/Admin/UserManagement/UserManagement"));
const ManageUser = lazy(() => import("../pages/Admin/UserManagement/ManageUser/ManageUser"));
const ManageUserListing = lazy(() => import("../pages/Admin/UserManagement/ManageUser/ManageUserListing/ManageUserListing"));
const ManageUserForm = lazy(() => import("../pages/Admin/UserManagement/ManageUser/ManageUserForm/ManageUserForm"));
const ManageUserView = lazy(() => import("../pages/Admin/UserManagement/ManageUser/ManageUserView/ManageUserView"));
const ManageRole = lazy(() => import("../pages/Admin/UserManagement/ManageRole/ManageRole"));
const ManageRoleListing = lazy(() => import("../pages/Admin/UserManagement/ManageRole/ManageRoleListing/ManageRoleListing"));
const ManageRoleForm = lazy(() => import("../pages/Admin/UserManagement/ManageRole/ManageRoleForm/ManageRoleForm"));
const ManageRoleView = lazy(() => import("../pages/Admin/UserManagement/ManageRole/ManageRoleView/ManageRoleView"));
const ManageInterviewer = lazy(() => import("../pages/Admin/UserManagement/ManageInterviewer/ManageInterviewer"));
const ManageInterviewerForm = lazy(() => import("../pages/Admin/UserManagement/ManageInterviewer/ManageInterviewerForm/ManageInterviewerForm"));
const ManageInterviewerListing = lazy(() => import("../pages/Admin/UserManagement/ManageInterviewer/ManageInterviewerListing/ManageInterviewerListing"));
const ManageInterviewerView = lazy(() => import("../pages/Admin/UserManagement/ManageInterviewer/ManageInterviewerView/ManageInterviewerView"));
const ManageInterviewerSlot = lazy(() => import("../pages/Admin/UserManagement/ManageInterviewer/ManageInterviewerSlot/ManageInterviewerSlot"));
const ManageInterviewerCalendar = lazy(() => import("../pages/Admin/UserManagement/ManageInterviewer/ManageInterviewCalendar/ManageInterviewerCalendar"));
const InterviewerCalendarUpcomingInterview = lazy(() => import("../pages/Admin/UserManagement/ManageInterviewer/ManageInterviewCalendar/UpcomingInterview"))
const MasterSetting = lazy(() => import("../pages/Admin/MasterSetting/MasterSetting"));
const DomainMaster = lazy(() => import("../pages/Admin/MasterSetting/DomainMaster/DomainMaster"));
const DomainMasterListing = lazy(() => import("../pages/Admin/MasterSetting/DomainMaster/DomainMasterListing/ManageUserListing"));
const DomainMasterForm = lazy(() => import("../pages/Admin/MasterSetting/DomainMaster/DomainMasterForm/DomainMasterForm"));
const DomainMasterView = lazy(() => import("../pages/Admin/MasterSetting/DomainMaster/DomainMasterView/DomainMasterView"));
const RuleMaster = lazy(() => import("../pages/Admin/MasterSetting/RuleMaster/RuleMaster"));
const RuleMasterListing = lazy(() => import("../pages/Admin/MasterSetting/RuleMaster/RuleMasterListing/RuleMasterListing"));
const CandidateManagement = lazy(() => import("../pages/Admin/CandidateManagement/CandidateManagement"));
const CandidateManagementListing = lazy(() => import("../pages/Admin/CandidateManagement/CandidateManagementListing/CandidateManagementListing"));
const CandidateManagementView = lazy(() => import("../pages/Admin/CandidateManagement/CandidateManagementView/CandidateManagementView"));
const SystemLogs = lazy(() => import("../pages/Admin/SystemLogs/SystemLogs"));
const DocumentManagement = lazy(() => import("../pages/Admin/DocumentManagement/DocumentManagement"));
const DocumentManagementListing = lazy(() => import("../pages/Admin/DocumentManagement/DocumentManagementListing/DocumentManagementListing"));
const DocumentManagementForm = lazy(() => import("../pages/Admin/DocumentManagement/DocumentManagementForm/DocumentManagementForm"));
const PositionMasterListing = lazy(() => import("../pages/Admin/MasterSetting/PositionMaster/PositionMasterListing/PositionMasterListing"));
const PositionMaster = lazy(() => import("../pages/Admin/MasterSetting/PositionMaster/PositionMaster"));
const PositionMasterForm = lazy(() => import("../pages/Admin/MasterSetting/PositionMaster/PositionMasterForm/PositionMasterForm"));
const PositionMasterView = lazy(() => import("../pages/Admin/MasterSetting/PositionMaster/PositionMasterView/PositionMasterView"));
const PositionMasterMapEligibility = lazy(() => import("../pages/Admin/MasterSetting/PositionMaster/PositionMasterEligibility/PositionMasterMapEligibility"));
const AddCandidateFeedback = lazy(() => import("../pages/Admin/CandidateManagement/AddCandidateFeedback/AddCandidateFeedback"));
const ApplyPositionForm = lazy(() => import("../pages/Admin/CandidateManagement/ApplyPosition/ApplyPositionForm"));
const ApplyPositionView = lazy(() => import("../pages/Admin/CandidateManagement/ApplyPosition/ApplyPositionView"));
const ViewCV = lazy(() => import("../pages/Admin/Interviewer/InterviewTrackerListing/ViewCV/ViewCV"));
const QueryManagement = lazy(() => import("../pages/Admin/QueryManagement/QueryManagement"));
const QueryManagementListing = lazy(() => import("../pages/Admin/QueryManagement/QueryManagementListing/QueryManagementListing"));
const QueryManagementView = lazy(() => import("../pages/Admin/QueryManagement/QueryManagementView/QueryManagementView"));

const Interviewer = lazy(() => import("../pages/Admin/Interviewer/Interviewer"));
const InterviewTrackerListing = lazy(() => import("../pages/Admin/Interviewer/InterviewTrackerListing/InterviewTracker"));
const InterviewSlots = lazy(() => import("../pages/Admin/Interviewer/InterviewSlotListing/InterviewSlots"));

const Reports = lazy(() => import("../pages/Admin/Reports/Reports"));
const CandidatePositionReport = lazy(() => import("../pages/Admin/Reports/CandidatePositionReport"));
const CandidateInfraconReport = lazy(() => import("../pages/Admin/Reports/CandidateInfraconReport"));
const CandidateInterviewReport = lazy(() => import("../pages/Admin/Reports/CandidateInterviewReport"));
const InterviewsReport = lazy(() => import("../pages/Admin/Reports/InterviewsReport"));
const InterviewSlotReport = lazy(() => import("../pages/Admin/Reports/InterviewSlotReport/InterviewSlotReport"));
const InvoiceReport = lazy(() => import("../pages/Admin/Reports/InvoiceReport"));
const DownloadInvoice = lazy(() => import("../pages/Admin/Interviewer/DownloadInvoice/DownloadInvoice"));
const OffDay = lazy(() => import("../pages/Admin/MasterSetting/OffDay/OffDay"));

const InterviewTrackerFeedback = lazy(() => import("./../pages/Admin/InterviewTracker/InteviewTrackerListing/InterviewTrackerFeedback"));

const ProductedRoute = ({ roleCode }: any) => {
    const userData: any = !!sessionStorage.getItem("data") ? JSON.parse(sessionStorage.getItem("data") as any) : null;
    return decryptAESData(userData?.roles)?.role_code == roleCode ? <Outlet /> : <Navigate to='/' />
}

function AppRoutes() {
    const routes = useRoutes([

        {
            path: '',
            element: <Provider store={SkipToMainContentStore}> <WebLayout /></Provider>,
            children: [
                { path: "", element: <HomePage /> },
                { path: "about-us", element: <AboutUsPage /> },
                { path: "contact-us", element: <ContactUsPage /> },
                {
                    path: "explore-positions", element: <ProductedRoute roleCode="candidate" />,
                    children: [
                        { path: "", element: <ExplorePositionsPage /> }
                    ]
                },
                { path: "faqs", element: <FAQs /> },
                { path: "sop", element: <HomePage /> },
                { path: "terms", element: <TermsPage /> },
                { path: "disclaimer", element: <Disclaimer /> },
                { path: "copyrightpolicy", element: <CopyrightPolicy /> },
                { path: "hyperlinkingpolicy", element: <HyperLinkingPolicy /> },
                { path: "privacypolicy", element: <PrivacyPolicy /> },
                { path: "linksbyotherwebsites", element: <LinksByOtherWebsites /> },
            ],
        },
        {
            path: 'auth',
            element: <AuthLayout />,
            children: [
                { path: 'login', element: <LoginPage /> },
                { path: 'forgot-password', element: <ForgotPassword /> },
                { path: 'reset-password', element: <RestPassword /> },
            ],
        },
        {
            path: 'admin',
            element: <ProtectedRoute />,
            children: [
                { path: 'change-password', element: <AdminChangePassword /> },
                { path: 'profile', element: <Profile /> },

                {
                    path: '',
                    element: <AdminLayout />,
                    children: [
                        { path: 'dashboard', element: <Dashboard /> },
                        { path: 'notifications', element: <SystemNotificationListing /> },
                        { path: 'change-password', element: <AdminChangePassword /> },
                        {
                            path: 'user-management', element: <CheckPermission roleCode="administrator"><UserManagement /></CheckPermission>,
                            children: [
                                {
                                    path: 'manage-user', element: <ManageUser />, children: [
                                        { path: '', element: <ManageUserListing /> },
                                        { path: 'add', element: <ManageUserForm type="add" /> },
                                        { path: 'edit', element: <ManageUserForm type="edit" /> },
                                        { path: 'view', element: <ManageUserView /> },
                                    ]
                                },
                                {
                                    path: 'manage-role', element: <ManageRole />,
                                    children: [
                                        { path: '', element: <ManageRoleListing /> },
                                        { path: 'add', element: <ManageRoleForm type="add" /> },
                                        { path: 'edit', element: <ManageRoleForm type="edit" /> },
                                        { path: 'view', element: <ManageRoleView /> },
                                    ]
                                },
                                {
                                    path: 'manage-interviewer', element: <ManageInterviewer />,
                                    children: [
                                        { path: '', element: <ManageInterviewerListing /> },
                                        { path: 'add', element: <ManageInterviewerForm type="add" /> },
                                        { path: "edit/:id", element: <ManageInterviewerForm type="edit" /> },
                                        { path: 'view', element: <ManageInterviewerView /> },
                                        { path: "calendar", element: <ManageInterviewerCalendar /> },
                                        { path: "upcoming-interview-view/:id", element: <InterviewerCalendarUpcomingInterview /> },
                                        { path: 'interviewer-slot', element: <ManageInterviewerSlot /> },
                                    ]
                                },
                            ],
                        },
                        {
                            path: 'master-setting', element: <CheckPermission roleCode="administrator"><MasterSetting /></CheckPermission>,
                            children: [
                                {
                                    path: 'domain-master', element: <DomainMaster />,
                                    children: [
                                        { path: '', element: <DomainMasterListing /> },
                                        { path: 'add', element: <DomainMasterForm type="add" /> },
                                        { path: 'edit', element: <DomainMasterForm type="edit" /> },
                                        { path: 'view', element: <DomainMasterView /> },
                                    ]
                                },
                                {
                                    path: 'position-master', element: <PositionMaster />,
                                    children: [
                                        { path: '', element: <PositionMasterListing /> },
                                        { path: 'add', element: <PositionMasterForm type="add" /> },
                                        { path: 'edit', element: <PositionMasterForm type="edit" /> },
                                        { path: 'view', element: <PositionMasterView /> },
                                        { path: 'map-eligibility', element: <PositionMasterMapEligibility /> },
                                    ]
                                },
                                {
                                    path: 'rule-master', element: <RuleMaster />,
                                    children: [
                                        { path: '', element: <RuleMasterListing /> },
                                    ]
                                },
                                {
                                    path: 'off-day',
                                    element: <OffDay />,
                                },
                            ],
                        },
                        {
                            path: 'user-manual', element: <CheckPermission roleCode="administrator" path={'/admin/user-manual'}><DocumentManagement /></CheckPermission>,
                            children: [
                                { path: '', element: <DocumentManagementListing /> },
                                { path: 'add', element: <DocumentManagementForm type="add" /> },
                                { path: 'edit', element: <DocumentManagementForm type="edit" /> }
                            ],
                        },
                        {
                            path: 'candidate-management', element: <CheckPermission roleCode={["administrator", "interviewer"]}><CandidateManagement /></CheckPermission>,
                            children: [
                                { path: "", element: <CandidateManagementListing /> },
                                { path: "add", element: <CheckPermission roleCode="administrator"><ApplyPositionForm type="add" /></CheckPermission> },
                                { path: "edit", element: <CheckPermission roleCode="administrator"><ApplyPositionForm type="edit" /></CheckPermission> },
                                { path: "apply-position", element: <CheckPermission roleCode="administrator"><ApplyPositionView /></CheckPermission> },
                                { path: "view", element: <CheckPermission roleCode="administrator"><CandidateManagementView /></CheckPermission>, },
                                { path: "add-feedback/:id/:panelId", element: <CheckPermission roleCode="interviewer"><AddCandidateFeedback type="add" /></CheckPermission> },
                                { path: "edit-feedback/:id", element: <CheckPermission roleCode="interviewer"><AddCandidateFeedback type="edit" /></CheckPermission> },
                                { path: "view-feedback/:id", element: <CheckPermission roleCode="interviewer"><AddCandidateFeedback type="view" /></CheckPermission> },
                                { path: 'view/upcoming-interview', element: <CandidateManagementUpcomingView /> },
                                { path: 'view/reschedule-interview', element: <CandidateManageMentRescheduleInterview /> },
                                { path: 'view/position', element: <CandidateMangementPositionView /> },
                                { path: 'calender-view', element: <CandidateManagementCalenderView /> },


                            ]
                        },
                        { path: 'system-logs', element: <SystemLogs /> },
                        {
                            path: 'query-management',
                            element: <CheckPermission roleCode="administrator"><QueryManagement /></CheckPermission>,
                            children: [
                                { path: "", element: <QueryManagementListing /> },
                                { path: "view", element: <QueryManagementView /> },
                            ],
                        },
                        {
                            path: 'interviewer',
                            element: <CheckPermission roleCode="interviewer"><Interviewer /></CheckPermission>,
                            children: [
                                {
                                    path: "interview-tracker", element: <Interviewer />,
                                    children: [
                                        { path: "", element: <InterviewTrackerListing /> },
                                        { path: 'view', element: <InterviewTrackerView /> }
                                    ]
                                },
                                { path: "interview-slots", element: <CheckPermission roleCode="interviewer" path={'/admin/interviewer/interview-slots'}><InterviewSlots /></CheckPermission> },
                                { path: "candidate-management", element: <CandidateManagementListing /> },
                            ]
                        },
                        { path: "download-invoice", element: <CheckPermission roleCode="interviewer" path={'/admin/interviewer/download-invoice'}><DownloadInvoice /></CheckPermission> },
                        { path: "viewCV", element: <CheckPermission roleCode={["administrator", "interviewer"]}><ViewCV /></CheckPermission> },
                        {

                            path: 'position-tracker',
                            element: <CheckPermission roleCode="candidate" path="/position-tracker"><PositionTracker /></CheckPermission>,
                            children: [
                                { path: "", element: <PositionTrackerListing /> },
                                { path: "raise-query", element: <RaiseQuery /> }
                            ]
                        },
                        {
                            path: 'interview-calendar',
                            element: <CheckPermission roleCode="candidate"><InterviewCalendar /></CheckPermission>,
                            children: [
                                { path: "", element: <InterviewCalendarList /> },
                                { path: "view", element: <InterviewCalendarView /> }
                            ]
                        },
                        {
                            path: 'query-tracker',
                            element: <CheckPermission roleCode={["candidate", "administrator"]}><QueryTracker /></CheckPermission>,
                            children: [
                                {
                                    path: '', element: <QueryTrackerListing />
                                },
                                { path: "view", element: <QueryTrackerView /> },
                                { path: "respond/:id", element: <QueryTrackerRespond /> },
                                { path: "respond/:id/view", element: <QueryTrackerRespond type={"view"} /> },
                                {
                                    path: 'eligibility-comparison/:candidate_id/:position_eligibility_id',
                                    element: <EligiblityComparision />
                                }

                            ]
                        },
                        {
                            path: 'sop-guideline',
                            element: <SopAndGuideline />,
                            children: [
                                {
                                    path: '', element: <SopAndGuidelineListing />
                                }
                            ]
                        },
                        {
                            path: 'interview-tracker',
                            element: <CheckPermission roleCode="administrator"><InterviewTrackers /></CheckPermission>,
                            children: [
                                {
                                    path: '',
                                    element: <InterviewTrackerListings />
                                },
                                {
                                    path: 'view/:id',
                                    element: <InterviewTrackerViews />
                                },
                                {
                                    path: 'feed-back/:id',
                                    element: <InterviewTrackerFeedback />
                                },
                                {
                                    path: 'eligibility-comparison/:candidate_id/:position_eligibility_id',
                                    element: <EligiblityComparision />
                                }
                            ]
                        },
                        {
                            path: 'rescheduling-tracker',
                            element: <CheckPermission roleCode="administrator"><InterviewTrackers /></CheckPermission>,
                            children: [
                                {
                                    path: '',
                                    element: <InterviewTrackerListings />
                                },
                                {
                                    path: 'view/:id',
                                    element: <InterviewTrackerViews />
                                },
                            ]
                        },
                        {
                            path: 'candidate-pool',
                            element: <CheckPermission roleCode="bidder"><CandidatePool /></CheckPermission>,
                        },
                        {
                            path: 'candidate-interview-tracker',
                            element: <CheckPermission roleCode="candidate"><CandidateInterviewTracker /></CheckPermission>,
                            children: [
                                {
                                    path: "",
                                    element: <CandidateInterviewTrackerListing />
                                },
                                {
                                    path: 'view/:id',
                                    element: <CandidateInterviewTrackerView />
                                },
                            ]
                        },
                        {
                            path: 'reports',
                            element: <CheckPermission roleCode="administrator"><Reports /></CheckPermission>,
                            children: [
                                {
                                    path: 'candidate-position-report',
                                    element: <CandidatePositionReport />,
                                },
                                {
                                    path: 'candidate-infracon-report',
                                    element: <CandidateInfraconReport />,
                                },
                                {
                                    path: 'candidate-interviewer-report',
                                    element: <InterviewsReport />
                                },
                                {
                                    path: 'interview-slot-report',
                                    element: <InterviewSlotReport />
                                },
                                {
                                    path: 'candidate-interview-report',
                                    element: <CandidateInterviewReport />,
                                },
                                {
                                    path: 'invoice-report',
                                    element: <InvoiceReport />,
                                }
                            ]
                        },
                    ],
                }
            ]
        },
    ])


    return (

        <Suspense fallback={<PageLoader />}>
            {routes}
        </Suspense>
    )

}

export default AppRoutes;